/*@flow*/
import type { Node } from 'react'; /** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Fragment, useState } from 'react';
import useInit from './useInit';
import { UPDATE_DISTRICT, refetchQueries, CREATE_DISTRICT } from './queries';
import { useMutation } from '@apollo/react-hooks';
import * as I from 'immutable';
import useCommands from './useCommands';
import useContextState from './useContextState';

export default (props: { disabled?: boolean }): Node => {
  const s = useContextState();
  const { areaWorkingCopy } = s;
  const cmd = useCommands();
  const initLoading = useInit(['SaveBtn', 'isLoading'], false);
  const [isLoading, setLoading] = useState(initLoading);
  const initErrors = useInit(['SaveBtn', 'errors'], null);
  const [errors, setErrors] = useState(initErrors);
  const { id } = areaWorkingCopy;
  const opts = {
    variables: areaWorkingCopy.toJS(),
    refetchQueries,
  };
  const [save] = useMutation(UPDATE_DISTRICT, opts);
  const [create] = useMutation(CREATE_DISTRICT, opts);

  const isDisabled = isLoading || props.disabled;

  const onError = (err) => {
    if (err.graphQLErrors) {
      setErrors(err.graphQLErrors.map((v) => v.message));
    } else if (err.message) {
      setErrors([err.message]);
    } else if (Array.isArray(err)) {
      setErrors(err);
    } else {
      setErrors([err]);
    }
    setLoading(false);
  };

  return (
    <Fragment>
      {errors && errors.map && (
        <span css={css('max-width: 200px;')} className="text-danger">
          Error: {errors}
        </span>
      )}
      <div className="ml-auto mt-1">
        <button
          className="btn-link"
          disabled={isDisabled}
          onClick={() => {
            if (isDisabled) return;
            if (isLoading) return;
            setLoading(true);

            const isUpdate = Boolean(id);

            // If the id doesn't exist run create. Otherwise, run save.
            if (isUpdate) {
              save()
                .then((payload) => {
                  setLoading(false);
                  const key = 'districtUpdate';
                  const res = I.getIn(payload, ['data', key], {});
                  if (res.success) {
                    // Clear errors
                    setErrors(null);
                    // Close the popover.
                    cmd.setEditOverlay(false);
                    // Copy working copy to backup
                    cmd.areaSaved(res.record);
                  } else {
                    setErrors(res.errors);
                  }
                })
                .catch(onError);
            } else {
              create()
                .then((payload) => {
                  setLoading(false);
                  const key = 'districtCreate';
                  const res = I.getIn(payload, ['data', key], {});
                  if (res.success) {
                    // Clear errors
                    setErrors(null);
                    // Copy working copy to backup
                    cmd.areaSaved(res.record);
                  } else {
                    setErrors(res.errors);
                  }
                })
                .catch(onError);
            }
          }}
          data-testid="SaveButton"
        >
          {isLoading ? (
            <i className="fas fa-circle-notch fa-spin" data-testid="Spinner" />
          ) : (
            <i className="fa fa-save" />
          )}{' '}
          Save
        </button>
      </div>
    </Fragment>
  );
};
